// SAM-1095 — English | Español tabs for the CMS editors (mocks C3/C4). Write in English; Español is translated by
// itself until it is edited by hand, with Undo and Translate again. Same mechanism as BilingualField (utils/translate).
import React, { useEffect, useRef, useState } from 'react';
import { RotateCcw, Sparkles } from 'lucide-react';
import { translate } from '../../utils/translate';
import { SEMICOLON_MESSAGE, cleanCrudText, hasSemicolon } from '../../utils/crudText';
import { useT } from '../../i18n/admin';

type Lang = 'en' | 'es';

export interface BilingualSpec {
  /** Form keys are `${base}_en` / `${base}_es`. */
  base: string;
  label: string;
  multiline?: boolean;
  minHeight?: number;
  placeholder?: string;
  /** Small grey line under the field, e.g. a character counter. */
  note?: (value: string, lang: Lang) => string;
  error?: string;
}

interface Props {
  fields: BilingualSpec[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  /** Pause between the last keystroke and the translation. */
  debounceMs?: number;
}

const key = (base: string, lang: Lang) => `${base}_${lang}`;

const chip = (tone: 'ok' | 'warn' | 'idle') =>
  ({ ok: { background: '#E6F2EB', color: '#0E8345' }, warn: { background: '#FDF3DC', color: '#8A5A00' }, idle: { background: '#F3F4F6', color: '#6B7280' } })[tone];

export const BilingualPair: React.FC<Props> = ({ fields, values, onChange, disabled, debounceMs = 700 }) => {
  const { fmt } = useT();
  const [tab, setTab] = useState<Lang>('en');
  // Español fields that already had text when the editor opened, or that were typed by hand: never overwritten.
  const [manual, setManual] = useState<Set<string>>(() => new Set(fields.filter((f) => (values[key(f.base, 'es')] || '').trim()).map((f) => f.base)));
  const [auto, setAuto] = useState<{ at: Date; prev: Record<string, string> } | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const lastEn = useRef<Record<string, string>>(Object.fromEntries(fields.map((f) => [f.base, (values[key(f.base, 'en')] || '').trim()])));
  const timers = useRef<Record<string, number>>({});
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const run = async (base: string): Promise<boolean> => {
    const en = (valuesRef.current[key(base, 'en')] || '').trim();
    if (!en) return false;
    const es = cleanCrudText(await translate(en, 'en', 'es'));
    // translate() hands the original back when the service fails: do not pass that off as a translation.
    if (!es.trim() || es.trim() === cleanCrudText(en)) { setFailed(true); return false; }
    setFailed(false);
    setAuto((a) => ({ at: new Date(), prev: { [base]: valuesRef.current[key(base, 'es')] || '', ...(a?.prev ?? {}) } }));
    onChange(key(base, 'es'), es);
    return true;
  };

  useEffect(() => {
    fields.forEach((f) => {
      const en = (values[key(f.base, 'en')] || '').trim();
      if (en === lastEn.current[f.base]) return;
      lastEn.current[f.base] = en;
      window.clearTimeout(timers.current[f.base]);
      if (!en || manual.has(f.base)) return;
      timers.current[f.base] = window.setTimeout(() => { void run(f.base); }, debounceMs);
    });
    // run() reads the latest values through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => () => { Object.values(timers.current).forEach((t) => window.clearTimeout(t)); }, []);

  const edit = (f: BilingualSpec, lang: Lang, v: string) => {
    if (lang === 'es') setManual((m) => new Set(m).add(f.base));
    onChange(key(f.base, lang), v);
  };

  const undo = () => {
    if (!auto) return;
    Object.entries(auto.prev).forEach(([base, prev]) => onChange(key(base, 'es'), prev));
    setManual(new Set(Object.entries(auto.prev).filter(([, p]) => p.trim()).map(([b]) => b)));
    setAuto(null);
  };

  const again = async () => {
    setBusy(true);
    // A new translation starts a new history: Undo goes back to what was there before this one.
    setAuto(null);
    setManual(new Set());
    await Promise.all(fields.map((f) => run(f.base)));
    setBusy(false);
  };

  const enDone = fields.every((f) => (values[key(f.base, 'en')] || '').trim());
  const esAny = fields.some((f) => (values[key(f.base, 'es')] || '').trim());
  const esDone = fields.every((f) => (values[key(f.base, 'es')] || '').trim());
  const esChip = !esAny ? { text: 'Empty', tone: 'idle' as const } : auto ? { text: 'Translated automatically · review', tone: 'warn' as const } : esDone ? { text: 'Done', tone: 'ok' as const } : { text: 'Incomplete', tone: 'warn' as const };

  const tabBtn = (lang: Lang, text: string, c: { text: string; tone: 'ok' | 'warn' | 'idle' }) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === lang}
      onClick={() => setTab(lang)}
      className="inline-flex items-center gap-2 border-0 bg-transparent px-[18px] text-[15px] font-bold"
      style={{ height: 46, color: tab === lang ? 'var(--tenant-primary, #0B5C4D)' : '#5E5E5E', borderBottom: `3px solid ${tab === lang ? 'var(--tenant-primary, #0B5C4D)' : 'transparent'}` }}
    >
      {text}
      <span className="whitespace-nowrap rounded-full px-[9px] py-[3px] text-[11px] font-semibold" style={chip(c.tone)}>{c.text}</span>
    </button>
  );

  return (
    <div className="flex flex-col gap-3.5">
      <div role="tablist" className="flex border-b border-gray-200">
        {tabBtn('en', 'English', { text: enDone ? 'Done' : 'Required', tone: enDone ? 'ok' : 'warn' })}
        {tabBtn('es', 'Español', esChip)}
      </div>

      {tab === 'es' && auto && (
        <div className="flex flex-wrap items-center gap-2 text-[13px] text-gray-600">
          <span className="flex" style={{ color: 'var(--tenant-primary, #0B5C4D)' }}><Sparkles size={14} aria-hidden /></span>
          Translated from English automatically · {fmt.dateTime(auto.at)}
          <span className="text-gray-300">·</span>
          <button type="button" onClick={undo} className="border-0 bg-transparent p-0 font-semibold" style={{ color: 'var(--tenant-primary, #0B5C4D)' }}>Undo</button>
          <span className="text-gray-300">·</span>
          <button type="button" disabled={busy || disabled} onClick={again} className="inline-flex items-center gap-1 border-0 bg-transparent p-0 font-semibold disabled:opacity-50" style={{ color: 'var(--tenant-primary, #0B5C4D)' }}>
            <RotateCcw size={12} aria-hidden /> Translate again
          </button>
        </div>
      )}
      {tab === 'es' && failed && (
        <div role="status" className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
          The translation service did not answer. Write the Español text by hand, or try Translate again.
        </div>
      )}
      {tab === 'es' && !auto && esAny && (
        <button type="button" disabled={busy || disabled} onClick={again} className="inline-flex items-center gap-1 self-start border-0 bg-transparent p-0 text-[13px] font-semibold disabled:opacity-50" style={{ color: 'var(--tenant-primary, #0B5C4D)' }}>
          <RotateCcw size={12} aria-hidden /> Translate again from English
        </button>
      )}

      {fields.map((f) => {
        const k = key(f.base, tab);
        const v = values[k] || '';
        const common = {
          id: `cms-${k}`,
          value: v,
          disabled,
          placeholder: f.placeholder,
          'aria-invalid': f.error && tab === 'en' ? true : undefined,
          onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => edit(f, tab, e.target.value),
          className: 'w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900',
        };
        const note = f.note?.(v, tab);
        return (
          <div key={f.base} className="flex min-w-0 flex-col gap-1.5">
            <label htmlFor={`cms-${k}`} className="text-xs font-semibold text-gray-500">{f.label}</label>
            {f.multiline ? (
              <textarea {...common} rows={6} style={{ minHeight: f.minHeight ?? 96, padding: '10px 12px', lineHeight: 1.5 }} />
            ) : (
              <input {...common} type="text" style={{ height: 40 }} />
            )}
            {note && <span className="text-xs text-gray-500">{note}</span>}
            {hasSemicolon(v) ? <span role="alert" className="text-xs text-red-600">{SEMICOLON_MESSAGE}</span> : f.error && tab === 'en' && <span className="text-xs text-red-600">{f.error}</span>}
          </div>
        );
      })}
      <p className="m-0 text-xs text-gray-500">Typing in English translates to Español by itself until you edit the Español text by hand.</p>
    </div>
  );
};
